import { motion } from "framer-motion";
import { ExternalLink, Check, Star } from "lucide-react";
import { books } from "@/data/books";

function BookCard({ book, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: index * 0.1 }}
      data-testid={`book-card-${book.id}`}
      className="flex flex-col bg-white border-2 border-[#073B4C] rounded-[2rem] shadow-pop-lg overflow-hidden hover:-translate-y-1.5 transition-transform"
    >
      <div className="grid sm:grid-cols-2 gap-0">
        <div className="relative border-b-2 sm:border-b-0 sm:border-r-2 border-[#073B4C] bg-[#0d0d12]">
          <img
            src={book.cover}
            alt={book.title}
            loading="lazy"
            className="w-full h-full aspect-[3/4] object-contain"
          />
          <span
            className="absolute top-3 left-3 font-heading font-bold text-xs text-white border-2 border-[#073B4C] rounded-full px-3 py-1 shadow-pop"
            style={{ backgroundColor: book.accent }}
          >
            {book.tag}
          </span>
        </div>

        <div className="flex flex-col p-6 sm:p-7">
          <div className="flex items-center gap-2">
            <div className="flex text-[#FFD166]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={15} fill="#FFD166" strokeWidth={0} />
              ))}
            </div>
            <span className="font-body font-bold text-xs text-[#073B4C]/60">
              {book.format}
            </span>
          </div>

          <h3 className="font-display text-2xl sm:text-3xl tracking-tight mt-2 text-[#073B4C]">
            {book.title}
          </h3>
          <p className="font-body text-sm text-[#073B4C]/70 mt-2">
            {book.subtitle}
          </p>

          <ul className="space-y-2 mt-4 flex-1">
            {book.highlights?.slice(0, 4).map((h) => (
              <li key={h} className="flex items-start gap-2 font-body text-sm text-[#073B4C]">
                <span className="mt-0.5 shrink-0 bg-[#06D6A0] text-[#073B4C] border-2 border-[#073B4C] rounded-full p-0.5">
                  <Check size={12} strokeWidth={3} />
                </span>
                {h}
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-3 mt-6">
            {book.price ? (
              <span className="font-heading font-bold text-2xl text-[#073B4C]">
                {book.price}
              </span>
            ) : (
              <span className="font-body font-bold text-sm text-[#073B4C]/60">
                See price on Amazon
              </span>
            )}
            <a
              href={book.amazon}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`amazon-buy-btn-${book.id}`}
              className="inline-flex items-center gap-2 font-heading font-semibold bg-[#FFD166] text-[#073B4C] border-2 border-[#073B4C] rounded-full px-5 py-3 shadow-pop hover:-translate-y-1 hover:rotate-2 transition-transform"
            >
              Buy on Amazon
              <ExternalLink size={16} strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function FeaturedBooks() {
  return (
    <section
      id="books"
      data-testid="featured-books-section"
      className="relative py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-heading font-semibold text-sm text-[#118AB2] uppercase tracking-wider">
            The Collection
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2 text-[#073B4C]">
            Available now on Amazon
          </h2>
          <p className="font-body text-lg text-[#073B4C]/70 mt-4">
            Grab your copy, pick up your pencils, and start coloring today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {books.map((book, i) => (
            <BookCard key={book.id} book={book} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
